import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Resource, UIComponent } from '../../types';

interface SortingGameComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

interface SortableItem {
  id: string;
  emoji: string;
  name: string;
  category: string;
  color?: string;
  size?: string;
  isSorted: boolean;
}

interface Category {
  id: string;
  name: string;
  color: string;
  emoji: string;
}

export const SortingGameComponent: React.FC<SortingGameComponentProps> = ({ 
  resource, 
  component, 
  onAction 
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<SortableItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [sortedItems, setSortedItems] = useState<Record<string, SortableItem[]>>({});
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverCategory, setDragOverCategory] = useState<string | null>(null);
  const [validationMessage, setValidationMessage] = useState<string>('');
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [allowCorrections, setAllowCorrections] = useState<boolean>(true);
  const [validationStats, setValidationStats] = useState<{correct: number, incorrect: number}>({correct: 0, incorrect: 0});
  const [currentSortingCriteria, setCurrentSortingCriteria] = useState<string>('category');
  const [criteriaChangeMessage, setCriteriaChangeMessage] = useState<string>('');

  // Initialize game from resource configuration
  useEffect(() => {
    const gameItems = (resource.items || [
      { emoji: '🐔', name: 'poule', category: 'animaux' },
      { emoji: '🐑', name: 'mouton', category: 'animaux' },
      { emoji: '🐦', name: 'canari', category: 'animaux' },
      { emoji: '🥄', name: 'pilon', category: 'objets' },
      { emoji: '💡', name: 'lampe', category: 'objets' },
      { emoji: '🏠', name: 'case', category: 'objets' },
      { emoji: '👒', name: 'chapeau', category: 'objets' }
    ]).map((item: any, index: number) => ({
      ...item,
      id: item.id || `item_${index}_${Date.now()}`,
      isSorted: false
    }));

    const gameCategories = resource.categories || [
      { id: 'animaux', name: 'Animaux', color: '#EF4444', emoji: '🐾' },
      { id: 'objets', name: 'Objets', color: '#3B82F6', emoji: '📦' },
      { id: 'rouge', name: 'Rouge', color: '#DC2626', emoji: '🔴' },
      { id: 'jaune', name: 'Jaune', color: '#F59E0B', emoji: '🟡' },
      { id: 'bleu', name: 'Bleu', color: '#2563EB', emoji: '🔵' },
      { id: 'blanc', name: 'Blanc', color: '#6B7280', emoji: '⚪' },
      { id: 'marron', name: 'Marron', color: '#92400E', emoji: '🟤' },
      { id: 'petit', name: 'Petit', color: '#10B981', emoji: '🔸' },
      { id: 'moyen', name: 'Moyen', color: '#F59E0B', emoji: '🔶' },
      { id: 'grand', name: 'Grand', color: '#EF4444', emoji: '🔷' }
    ];

    // Shuffle items
    const shuffledItems = gameItems.sort(() => Math.random() - 0.5);
    setItems(shuffledItems);
    setCategories(gameCategories);

    // Initialize sorted items
    const initialSorted: Record<string, SortableItem[]> = {};
    gameCategories.forEach((cat: Category) => {
      initialSorted[cat.id] = [];
    });
    setSortedItems(initialSorted);

    // Auto-start if configured
    if (resource.autoStart) {
      setGameStarted(true);
    }
  }, [resource.items, resource.categories, resource.autoStart]);

  const getItemCategoryByCriteria = (item: SortableItem, criteria: string): string => {
    switch (criteria) {
      case 'color':
        return item.color || item.category;
      case 'size':
        return item.size || item.category;
      case 'category':
      default:
        return item.category;
    }
  };

  const reorganizeSortedItems = (newCriteria: string) => {
    // Get all currently sorted items
    const allSortedItems = Object.values(sortedItems).flat();
    
    if (allSortedItems.length === 0) {
      // No items to reorganize
      return;
    }
    
    // Show informative message
    const criteriaNames = {
      'category': 'Nature',
      'color': 'Couleur',
      'size': 'Taille'
    };
    
    setCriteriaChangeMessage(`🔄 Réorganisation selon le critère : ${criteriaNames[newCriteria as keyof typeof criteriaNames]}`);
    
    // Hide message after 3 seconds
    setTimeout(() => {
      setCriteriaChangeMessage('');
    }, 3000);
    
    // Reorganize them according to the new criteria
    const newSortedItems: Record<string, SortableItem[]> = {};
    
    // Initialize empty arrays for all categories
    categories.forEach(cat => {
      newSortedItems[cat.id] = [];
    });
    
    // Redistribute items according to new criteria
    allSortedItems.forEach(item => {
      const correctCategory = getItemCategoryByCriteria(item, newCriteria);
      if (newSortedItems[correctCategory]) {
        newSortedItems[correctCategory].push(item);
      }
    });
    
    setSortedItems(newSortedItems);
  };

  const validateItemPlacement = (item: SortableItem, categoryId: string): boolean => {
    const correctCategory = getItemCategoryByCriteria(item, currentSortingCriteria);
    return correctCategory === categoryId;
  };

  const showValidationMessage = (message: string, isSuccess: boolean) => {
    setValidationMessage(message);
    setShowValidation(true);
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      setShowValidation(false);
    }, 3000);
  };

  const handleItemDrop = (itemId: string, categoryId: string) => {
    if (!gameStarted || gameCompleted) return;

    // Check if item is being moved from a category (correction)
    let item: SortableItem | undefined;
    let isCorrection = false;
    
    // First check if item is in unsorted items
    item = items.find(i => i.id === itemId);
    
    // If not found, check if it's being moved from a category
    if (!item) {
      for (const [, catItems] of Object.entries(sortedItems)) {
        const foundItem = catItems.find(i => i.id === itemId);
        if (foundItem) {
          item = foundItem;
          isCorrection = true;
          break;
        }
      }
    }

    if (!item) return;

    console.log('Dropping item:', item.name, 'into category:', categoryId, 'isCorrection:', isCorrection);

    // Validate placement
    const isValid = validateItemPlacement(item, categoryId);
    
    if (isValid) {
      // Correct placement
      if (isCorrection) {
        // Remove from current category
        const newSortedItems = { ...sortedItems };
        for (const [catId, catItems] of Object.entries(newSortedItems)) {
          newSortedItems[catId] = catItems.filter(i => i.id !== itemId);
        }
        // Add to new category
        newSortedItems[categoryId] = [...newSortedItems[categoryId], item];
        setSortedItems(newSortedItems);
      } else {
        // Remove from unsorted items
        const newItems = items.filter(i => i.id !== itemId);
        setItems(newItems);

        // Add to sorted category
        const newSortedItems = { ...sortedItems };
        newSortedItems[categoryId] = [...newSortedItems[categoryId], item];
        setSortedItems(newSortedItems);

        // Check if game is completed
        if (newItems.length === 0) {
          setGameCompleted(true);
          onAction?.('game_completed', { 
            totalItems: items.length,
            sortedByCategory: newSortedItems
          });
        }
      }

      // Show success message
      const categoryName = categories.find(c => c.id === categoryId)?.name || categoryId;
      showValidationMessage(`✅ Correct ! ${item.name} va bien dans ${categoryName}`, true);

      // Update validation stats
      setValidationStats(prev => ({ ...prev, correct: prev.correct + 1 }));

      onAction?.('item_sorted', { 
        itemId, 
        itemName: item.name,
        categoryId,
        categoryName: categories.find(c => c.id === categoryId)?.name,
        isValid: true,
        isCorrection
      });
    } else {
      // Incorrect placement
      const correctCategory = categories.find(c => c.id === item?.category);
      const attemptedCategory = categories.find(c => c.id === categoryId);
      
      if (allowCorrections && isCorrection) {
        // Allow correction but show warning
        showValidationMessage(
          `⚠️ Attention ! ${item?.name} ne va pas dans ${attemptedCategory?.name}. ${item?.name} va dans ${correctCategory?.name}`, 
          false
        );
        
        // Still move the item but mark as incorrect
        const newSortedItems = { ...sortedItems };
        for (const [catId, catItems] of Object.entries(newSortedItems)) {
          newSortedItems[catId] = catItems.filter(i => i.id !== itemId);
        }
        if (item) {
          newSortedItems[categoryId] = [...newSortedItems[categoryId], item];
        }
        setSortedItems(newSortedItems);
      } else {
        // Don't allow incorrect placement
        showValidationMessage(
          `❌ Non ! ${item?.name} ne va pas dans ${attemptedCategory?.name}. ${item?.name} va dans ${correctCategory?.name}`, 
          false
        );
      }

      // Update validation stats for incorrect placement
      setValidationStats(prev => ({ ...prev, incorrect: prev.incorrect + 1 }));

      onAction?.('item_sorted', { 
        itemId, 
        itemName: item?.name,
        categoryId,
        categoryName: categories.find(c => c.id === categoryId)?.name,
        isValid: false,
        correctCategory: item?.category,
        isCorrection
      });
    }

    // Clear drag states
    setDraggedItem(null);
    setDragOverCategory(null);
  };

  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    setDraggedItem(itemId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', itemId);
    console.log('Drag started for item:', itemId);
  };

  const handleDragOver = (e: React.DragEvent, categoryId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCategory(categoryId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverCategory(null);
  };

  const handleDrop = (e: React.DragEvent, categoryId: string) => {
    e.preventDefault();
    const itemId = e.dataTransfer.getData('text/plain');
    console.log('Drop event:', itemId, 'into', categoryId);
    handleItemDrop(itemId, categoryId);
  };

  const startGame = () => {
    setGameStarted(true);
    setGameCompleted(false);
    onAction?.('game_started', { totalItems: items.length });
  };

  const resetGame = () => {
    setGameStarted(false);
    setGameCompleted(false);
    
    // Reset items to unsorted state
    const originalItems = (resource.items || [
      { emoji: '🐔', name: 'poule', category: 'animaux' },
      { emoji: '🐑', name: 'mouton', category: 'animaux' },
      { emoji: '🐦', name: 'canari', category: 'animaux' },
      { emoji: '🥄', name: 'pilon', category: 'objets' },
      { emoji: '💡', name: 'lampe', category: 'objets' },
      { emoji: '🏠', name: 'case', category: 'objets' },
      { emoji: '👒', name: 'chapeau', category: 'objets' }
    ]).map((item: any, index: number) => ({
      ...item,
      id: item.id || `item_${index}_${Date.now()}`,
      isSorted: false
    }));
    
    const shuffledItems = originalItems.sort(() => Math.random() - 0.5);
    setItems(shuffledItems);
    
    // Clear sorted items
    const initialSorted: Record<string, SortableItem[]> = {};
    categories.forEach((cat: Category) => {
      initialSorted[cat.id] = [];
    });
    setSortedItems(initialSorted);

    // Reset validation stats and messages
    setValidationStats({correct: 0, incorrect: 0});
    setShowValidation(false);
    setValidationMessage('');
    
    // Only reset sorting criteria if explicitly requested
    // setCurrentSortingCriteria('category');

    onAction?.('game_reset', {});
  };

  const getCategoryStats = () => {
    const totalItems = Object.values(sortedItems).flat().length;
    const totalExpected = items.length + totalItems;
    return { sorted: totalItems, total: totalExpected };
  };

  return (
    <div className="bg-green-50 rounded-lg p-4">
      <div className="text-center mb-4">
        <h3 className="font-semibold text-green-900 mb-2">
          {resource.title || t('sortingGame.sortingGame')}
        </h3>
        <p className="text-sm text-green-700">
          {t('sortingGame.sortItemsIntoCategories')}
        </p>
      </div>

      {/* Game Controls */}
      <div className="flex justify-center gap-4 mb-4">
        {!gameStarted && (
          <button
            type="button"
            onClick={startGame}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            {t('sortingGame.startGame')}
          </button>
        )}
        
        {gameStarted && !gameCompleted && (
          <button
            type="button"
            onClick={resetGame}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            {t('sortingGame.resetGame')}
          </button>
        )}
      </div>

      {/* Game Stats */}
      {gameStarted && (
        <div className="text-center mb-4">
          <div className="inline-flex gap-4 bg-white rounded-lg px-4 py-2 border border-green-200">
            <span className="text-sm text-green-700">
              {t('sortingGame.sortedItems')}: {getCategoryStats().sorted} / {getCategoryStats().total}
            </span>
            <span className="text-sm text-green-700">
              {t('sortingGame.status')}: {gameCompleted ? t('sortingGame.completed') : t('sortingGame.inProgress')}
            </span>
          </div>
          
          {/* Validation Stats */}
          <div className="mt-2 inline-flex gap-4 bg-white rounded-lg px-4 py-2 border border-blue-200">
            <span className="text-sm text-green-600">
              ✅ Correct: {validationStats.correct}
            </span>
            <span className="text-sm text-red-600">
              ❌ Incorrect: {validationStats.incorrect}
            </span>
          </div>
          
          {/* Sorting Criteria Selector */}
          <div className="mt-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Critère de tri :
            </label>
            <select
              value={currentSortingCriteria}
              onChange={(e) => {
                const newCriteria = e.target.value;
                setCurrentSortingCriteria(newCriteria);
                // Reorganize existing sorted items according to new criteria
                reorganizeSortedItems(newCriteria);
              }}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
            >
              <option value="category">Nature (Animaux/Objets)</option>
              <option value="color">Couleur</option>
              <option value="size">Taille</option>
            </select>
          </div>
          
          {/* Correction Controls */}
          <div className="mt-2">
            <label className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={allowCorrections}
                onChange={(e) => setAllowCorrections(e.target.checked)}
                className="rounded border-gray-300"
              />
              Permettre les corrections
            </label>
          </div>
        </div>
      )}

      {/* Validation Message */}
      {showValidation && (
        <div className={`text-center mb-4 p-3 rounded-lg border-2 transition-all duration-300 ${
          validationMessage.includes('✅') 
            ? 'bg-green-100 border-green-300 text-green-800' 
            : 'bg-red-100 border-red-300 text-red-800'
        }`}>
          <div className="font-semibold text-sm">
            {validationMessage}
          </div>
        </div>
      )}

      {/* Criteria Change Message */}
      {criteriaChangeMessage && (
        <div className="text-center mb-4 p-3 rounded-lg border-2 bg-blue-100 border-blue-300 text-blue-800 transition-all duration-300">
          <div className="font-semibold text-sm">
            {criteriaChangeMessage}
          </div>
        </div>
      )}

      {/* Categories */}
      {gameStarted && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          {categories.map((category) => (
            <div
              key={category.id}
              className={`min-h-[120px] p-4 rounded-lg border-2 border-dashed transition-all duration-200 ${
                dragOverCategory === category.id ? 'scale-105 shadow-lg' : ''
              }`}
              style={{ 
                borderColor: category.color,
                backgroundColor: dragOverCategory === category.id ? `${category.color}20` : `${category.color}10`
              }}
              onDragOver={(e) => handleDragOver(e, category.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, category.id)}
            >
              <div className="text-center mb-2">
                <span className="text-2xl mr-2">{category.emoji}</span>
                <h4 className="font-semibold text-sm" style={{ color: category.color }}>
                  {category.name}
                </h4>
              </div>
              
              {/* Sorted items in this category */}
              <div className="flex flex-wrap gap-1 justify-center">
                {sortedItems[category.id]?.map((item) => (
                  <div
                    key={item.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, item.id)}
                    className={`text-2xl p-1 rounded border cursor-move transition-all duration-200 ${
                      draggedItem === item.id 
                        ? 'bg-blue-200 border-blue-500 scale-110 shadow-lg' 
                        : 'bg-white border-gray-300 hover:bg-gray-100'
                    }`}
                    title={item.name}
                  >
                    {item.emoji}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Unsorted Items */}
      {gameStarted && items.length > 0 && (
        <div className="bg-white rounded-lg p-4 border-2 border-green-300">
          <h4 className="text-center font-semibold text-green-900 mb-3">
            {t('sortingGame.dragItemsToCategories')}
          </h4>
          <div className="flex flex-wrap gap-2 justify-center">
            {items.map((item) => (
              <div
                key={item.id}
                draggable
                onDragStart={(e) => handleDragStart(e, item.id)}
                className={`text-3xl p-2 rounded-lg border-2 cursor-move transition-all duration-200 ${
                  draggedItem === item.id 
                    ? 'bg-blue-200 border-blue-500 scale-110 shadow-lg' 
                    : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                }`}
                title={item.name}
              >
                {item.emoji}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Game Completion Message */}
      {gameCompleted && (
        <div className="mt-4 p-4 bg-green-100 rounded-lg text-center">
          <div className="text-2xl mb-2">🎉</div>
          <h4 className="font-semibold text-green-900 mb-1">
            {t('sortingGame.congratulations')}
          </h4>
          <p className="text-sm text-green-800">
            {t('sortingGame.gameCompletedMessage')}
          </p>
        </div>
      )}
    </div>
  );
};
